import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hub, Auth } from 'aws-amplify';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES, CANDIDATE_REDIRECT_KEY } from '../../constants';

/**
 * Check the current URL for an `error_description` query-parameter that Cognito
 * appends when the pre-signup trigger rejects the first Google sign-in (account
 * merge flow).  Returns the decoded description string or `null`.
 */
const getOAuthErrorFromUrl = (): string | null => {
  const url = new URL(window.location.href);
  return url.searchParams.get('error_description');
};

/**
 * Returns `true` when the error is the pre-signup account-linking error that is
 * expected the very first time a user signs in with Google (Cognito merges the
 * federated identity with the username/password identity).
 */
const isAccountLinkingError = (errorDesc: string): boolean => {
  const accountLinkPatterns = [
    'Invalid lambda function output',
    'Account is setup',
    'PreSignUp failed',
  ];
  return accountLinkPatterns.some((p) =>
    errorDesc.toLowerCase().includes(p.toLowerCase()),
  );
};

/**
 * Handles the OAuth callback after Google sign-in.
 * Amplify automatically exchanges the authorization code for tokens.
 * This component listens for the Hub 'signIn' event and redirects the user
 * back to the stored job apply URL.
 *
 * If the pre-signup trigger throws (first-time Google sign-in account merge),
 * a friendly popup is shown asking the user to click Google sign-in again.
 */
const OAuthRedirect: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [showAccountLinkPopup, setShowAccountLinkPopup] = useState(false);
  const processed = useRef(false);

  useEffect(() => {
    // --- 1. Check for pre-signup / account-linking error in the URL --------
    const oauthError = getOAuthErrorFromUrl();
    if (oauthError) {
      // Clean the error params from the URL so the user doesn't see them
      window.history.replaceState(
        window.history.state,
        '',
        ROUTES.OAUTH_REDIRECT,
      );

      if (isAccountLinkingError(oauthError)) {
        setShowAccountLinkPopup(true);
      } else {
        // Unknown OAuth error — show generic message
        setError(oauthError);
      }
    } else {
      // --- 2. Normal OAuth flow — exchange code for tokens -----------------
      const handleRedirect = async () => {
        if (processed.current) return;
        processed.current = true;

        try {
          await Auth.currentAuthenticatedUser();
          await queryClient.invalidateQueries({
            queryKey: ['auth', 'currentUser'],
          });

          const redirectTo = localStorage.getItem(CANDIDATE_REDIRECT_KEY);
          localStorage.removeItem(CANDIDATE_REDIRECT_KEY);

          if (redirectTo) {
            navigate(redirectTo, { replace: true });
          } else {
            navigate(ROUTES.HOME, { replace: true });
          }
        } catch {
          // User might not be authenticated yet, listen for Hub events
          Hub.listen('auth', async (hubData) => {
            const { event } = hubData.payload;

            if (event === 'signIn' || event === 'cognitoHostedUI') {
              await queryClient.invalidateQueries({
                queryKey: ['auth', 'currentUser'],
              });

              const redirectTo = localStorage.getItem(CANDIDATE_REDIRECT_KEY);
              localStorage.removeItem(CANDIDATE_REDIRECT_KEY);

              if (redirectTo) {
                navigate(redirectTo, { replace: true });
              } else {
                navigate(ROUTES.HOME, { replace: true });
              }
            } else if (event === 'signOut' || event === 'oAuthSignOut') {
              setError('Sign in was cancelled.');
              navigate(ROUTES.CANDIDATE_SIGNIN, { replace: true });
            } else if (
              event === 'signIn_failure' ||
              event === 'cognitoHostedUI_failure'
            ) {
              setError('Sign in failed. Please try again.');
            }
          });
        }
      };

      handleRedirect();
    }

    return () => {
      Hub.remove('auth', () => {});
    };
  }, [navigate, queryClient]);

  /** Re-trigger Google sign-in from the popup */
  const handleGoogleSignInAgain = () => {
    // The CANDIDATE_ROLE_KEY and CANDIDATE_REDIRECT_KEY are already in
    // localStorage from the first attempt — just redirect to Cognito again.
    const domain = process.env.REACT_APP_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID;
    const redirectSignIn = process.env.REACT_APP_REDIRECT_SIGNIN;

    const googleAuthUrl =
      `https://${domain}/oauth2/authorize` +
      `?identity_provider=Google` +
      `&redirect_uri=${redirectSignIn}` +
      `&response_type=CODE` +
      `&client_id=${clientId}` +
      `&scope=aws.cognito.signin.user.admin%20email%20openid%20phone%20profile`;

    window.location.replace(googleAuthUrl);
  };

  const handleBackToSignIn = () => {
    // Preserve the redirect URL so it's still available after navigating back
    const redirectTo = localStorage.getItem(CANDIDATE_REDIRECT_KEY);
    const signInUrl = redirectTo
      ? `${ROUTES.CANDIDATE_SIGNIN}?redirectTo=${encodeURIComponent(redirectTo)}`
      : ROUTES.CANDIDATE_SIGNIN;
    navigate(signInUrl, { replace: true });
  };

  // ---------- Account-linking popup (first-time Google sign-in) ----------
  if (showAccountLinkPopup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB] px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-sm">
          {/* Success icon */}
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Almost There!
          </h2>
          <p className="mb-6 text-sm text-gray-600">
            Thank you for registering! To complete your setup, please sign in
            with Google again. This is a one-time step for new users.
          </p>

          <button
            type="button"
            onClick={handleGoogleSignInAgain}
            className="group mb-3 flex w-full items-center overflow-hidden rounded-lg border border-gray-200 transition-colors hover:border-[#8510DF]/30 hover:bg-gray-50"
          >
            <span className="flex h-full items-center justify-center border-r border-gray-200 px-3 py-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <span className="flex-1 py-3 text-center text-sm font-medium text-gray-700">
              Continue with Google
            </span>
          </button>

          <button
            type="button"
            onClick={handleBackToSignIn}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ---------- Generic error state ----------------------------------------
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB] px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-sm">
          <p className="mb-4 text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={handleBackToSignIn}
            className="text-sm font-medium text-[#8510DF] hover:text-[#6b0eb5]"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ---------- Loading state while processing OAuth -----------------------
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB]">
      <div className="text-center">
        <svg
          className="mx-auto mb-4 h-8 w-8 animate-spin text-[#8510DF]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <p className="text-sm text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default OAuthRedirect;
